import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Receipt as ReceiptIcon, Image as ImageIcon, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eur = (n) => `€${(Number(n) || 0).toFixed(2).replace(".", ",")}`;
const today = () => new Date().toISOString().slice(0, 10);

export const Expenses = () => {
  const [seasonId, setSeasonId] = useState("");
  const [players, setPlayers] = useState([]);
  const [members, setMembers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);

  const reloadAll = useCallback(async (sId) => {
    const id = sId || seasonId;
    if (!id) return;
    const [ex, pay, sum] = await Promise.all([
      axios.get(`${API}/expenses?season_id=${id}`),
      axios.get(`${API}/payments?season_id=${id}`),
      axios.get(`${API}/expenses/summary?season_id=${id}`),
    ]);
    setExpenses(ex.data);
    setPayments(pay.data);
    setSummary(sum.data);
  }, [seasonId]);

  useEffect(() => {
    (async () => {
      try {
        const seasons = (await axios.get(`${API}/seasons`)).data;
        if (!seasons.length) { setLoading(false); return; }
        const s = seasons[0];
        setSeasonId(s.id);
        const [pl, mem] = await Promise.all([
          axios.get(`${API}/players?season_id=${s.id}`),
          axios.get(`${API}/team/members`).catch(() => ({ data: { members: [] } })),
        ]);
        setPlayers(pl.data);
        setMembers(mem.data.members || []);
        await reloadAll(s.id);
      } catch (err) {
        toast.error("Kon teamuitgaven niet laden");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const deleteExpense = async (id) => {
    if (!window.confirm("Deze uitgave verwijderen?")) return;
    try {
      await axios.delete(`${API}/expenses/${id}`);
      toast.success("Uitgave verwijderd");
      await reloadAll();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Deze betaling verwijderen?")) return;
    try {
      await axios.delete(`${API}/payments/${id}`);
      toast.success("Betaling verwijderd");
      await reloadAll();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  const payerLabel = (e) => {
    if (e.paid_by_type === "manager") {
      const m = members.find((x) => x.user_id === e.paid_by_user_id);
      return m ? m.name : "Manager";
    }
    if (e.paid_by_type === "player") {
      const p = players.find((x) => x.id === e.paid_by_player_id);
      return p ? `${p.first_name} ${p.last_name}` : "Speler";
    }
    return e.paid_by_name || "-";
  };

  const playerName = (id) => {
    const p = players.find((x) => x.id === id);
    return p ? `${p.first_name} ${p.last_name}` : "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="expenses-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">TEAMUITGAVEN</h1>
          <p className="text-sm text-slate-500 mt-1">Houd bij wat er is uitgegeven en bereken wat elke speler moet betalen.</p>
        </div>
      </div>

      <Tabs defaultValue="summary">
        <TabsList>
          <TabsTrigger value="summary" data-testid="tab-summary">Overzicht</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Uitgaven</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Betalingen</TabsTrigger>
        </TabsList>

        {/* ---------------- Summary ---------------- */}
        <TabsContent value="summary" className="space-y-4">
          {summary && (
            <div className="grid gap-3 sm:grid-cols-4">
              <StatCard label="Totaal uitgegeven" value={eur(summary.total_expenses)} />
              <StatCard label="Waarvan borg" value={eur(summary.total_deposit)} hint="Wordt terugbetaald, telt niet mee voor de spelers" />
              <StatCard label="Kosten per speler" value={eur(summary.share_per_player)} hint={`${summary.player_count} spelers`} />
              <StatCard label="Nog te ontvangen" value={eur((summary.per_player || []).reduce((s, p) => s + Math.max(0, p.to_pay), 0))} />
            </div>
          )}
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Speler</th>
                  <th className="text-right px-4 py-2 font-medium">Kosten aandeel</th>
                  <th className="text-right px-4 py-2 font-medium">Voorgeschoten</th>
                  <th className="text-right px-4 py-2 font-medium">Betaald</th>
                  <th className="text-right px-4 py-2 font-medium">Openstaand</th>
                </tr>
              </thead>
              <tbody>
                {(summary?.per_player || []).map((row) => (
                  <tr key={row.player_id} className="border-t border-slate-100" data-testid={`summary-row-${row.player_id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{row.first_name} {row.last_name}</td>
                    <td className="px-4 py-2 text-right text-slate-700">{eur(row.share)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{eur(row.prepaid)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{eur(row.contributed)}</td>
                    <td className={`px-4 py-2 text-right font-semibold ${row.to_pay > 0.005 ? "text-rose-600" : row.to_pay < -0.005 ? "text-emerald-600" : "text-slate-500"}`}>
                      {eur(row.to_pay)}
                    </td>
                  </tr>
                ))}
                {(!summary || !summary.per_player?.length) && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Nog geen spelers of uitgaven.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---------------- Expenses ---------------- */}
        <TabsContent value="expenses" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingExpense(null); setExpenseDialogOpen(true); }} data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-1" /> Uitgave toevoegen
            </Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Datum</th>
                  <th className="text-left px-4 py-2 font-medium">Omschrijving</th>
                  <th className="text-left px-4 py-2 font-medium">Betaald door</th>
                  <th className="text-right px-4 py-2 font-medium">Bedrag</th>
                  <th className="text-center px-4 py-2 font-medium">Borg</th>
                  <th className="text-center px-4 py-2 font-medium">Bon</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{e.date}</td>
                    <td className="px-4 py-2 text-slate-900">
                      <button className="hover:underline text-left" onClick={() => { setEditingExpense(e); setExpenseDialogOpen(true); }}>
                        {e.description}
                      </button>
                      {e.note && <div className="text-xs text-slate-500 mt-0.5">{e.note}</div>}
                    </td>
                    <td className="px-4 py-2 text-slate-700">{payerLabel(e)}</td>
                    <td className="px-4 py-2 text-right font-medium">{eur(e.amount)}</td>
                    <td className="px-4 py-2 text-center">{e.is_deposit ? <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">Borg</span> : ""}</td>
                    <td className="px-4 py-2 text-center">
                      {e.receipt_data_url ? (
                        <a href={e.receipt_data_url} target="_blank" rel="noreferrer" className="inline-flex text-sky-600 hover:text-sky-700">
                          <ImageIcon className="w-4 h-4" />
                        </a>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingExpense(e); setExpenseDialogOpen(true); }}
                          data-testid={`edit-expense-${e.id}`}
                          title="Bewerken"
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteExpense(e.id)} data-testid={`delete-expense-${e.id}`} title="Verwijderen">
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!expenses.length && (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    <ReceiptIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Nog geen uitgaven. Klik op &quot;Uitgave toevoegen&quot;.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ---------------- Payments ---------------- */}
        <TabsContent value="payments" className="space-y-3">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingPayment(null); setPaymentDialogOpen(true); }} data-testid="add-payment-btn">
              <Plus className="w-4 h-4 mr-1" /> Betaling toevoegen
            </Button>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Datum</th>
                  <th className="text-left px-4 py-2 font-medium">Speler</th>
                  <th className="text-right px-4 py-2 font-medium">Bedrag</th>
                  <th className="text-left px-4 py-2 font-medium">Notitie</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap">{p.date}</td>
                    <td className="px-4 py-2 font-medium">{playerName(p.player_id)}</td>
                    <td className="px-4 py-2 text-right font-medium">{eur(p.amount)}</td>
                    <td className="px-4 py-2 text-slate-500">{p.note || "-"}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingPayment(p); setPaymentDialogOpen(true); }}
                          data-testid={`edit-payment-${p.id}`}
                          title="Bewerken"
                        >
                          <Edit2 className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePayment(p.id)} data-testid={`delete-payment-${p.id}`} title="Verwijderen">
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!payments.length && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">Nog geen betalingen ontvangen.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <ExpenseDialog
        open={expenseDialogOpen}
        onClose={() => setExpenseDialogOpen(false)}
        onSaved={reloadAll}
        seasonId={seasonId}
        players={players}
        members={members}
        editing={editingExpense}
      />
      <PaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        onSaved={reloadAll}
        seasonId={seasonId}
        players={players}
        editing={editingPayment}
      />
    </div>
  );
};

const StatCard = ({ label, value, hint }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="text-xs uppercase text-slate-500 font-medium">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
  </div>
);

// ---------------- Expense dialog ----------------
const ExpenseDialog = ({ open, onClose, onSaved, seasonId, players, members, editing }) => {
  const empty = useMemo(() => ({
    date: today(), description: "", amount: "", is_deposit: false,
    paid_by_type: "manager", paid_by_user_id: members[0]?.user_id || "",
    paid_by_player_id: "", paid_by_name: "", receipt_data_url: "", note: "",
  }), [members]);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...empty, ...editing, amount: String(editing.amount) } : empty);
  }, [open, editing, empty]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/expenses/upload-receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_data_url: r.data.data_url }));
      toast.success("Bon geüpload");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload mislukt");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.description.trim() || !form.amount) {
      toast.error("Vul een omschrijving en bedrag in");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        season_id: seasonId,
        date: form.date,
        description: form.description.trim(),
        amount: parseFloat(String(form.amount).replace(",", ".")),
        is_deposit: !!form.is_deposit,
        paid_by_type: form.paid_by_type,
        paid_by_user_id: form.paid_by_type === "manager" ? form.paid_by_user_id : null,
        paid_by_player_id: form.paid_by_type === "player" ? form.paid_by_player_id : null,
        paid_by_name: form.paid_by_type === "other" ? form.paid_by_name.trim() : null,
        receipt_data_url: form.receipt_data_url || null,
        note: form.note?.trim() || null,
      };
      if (editing) {
        await axios.put(`${API}/expenses/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/expenses`, payload);
      }
      toast.success("Uitgave opgeslagen");
      onClose();
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Uitgave bewerken" : "Nieuwe uitgave"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="expense-date" />
            </div>
            <div>
              <Label>Bedrag (€)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="expense-amount" />
            </div>
          </div>
          <div>
            <Label>Omschrijving</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="bv. Ballenset, feestje na de wedstrijd" data-testid="expense-description" />
          </div>
          <div>
            <Label>Wie heeft betaald?</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[{v:"manager",l:"Manager"},{v:"player",l:"Speler"},{v:"other",l:"Anders"}].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm({ ...form, paid_by_type: o.v })}
                  className={`px-3 py-1.5 rounded-md text-sm border ${form.paid_by_type === o.v ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-300"}`}
                  data-testid={`paid-by-${o.v}`}
                >{o.l}</button>
              ))}
            </div>
            {form.paid_by_type === "manager" && (
              <Select value={form.paid_by_user_id} onValueChange={(v) => setForm({ ...form, paid_by_user_id: v })}>
                <SelectTrigger className="mt-2" data-testid="manager-select"><SelectValue placeholder="Kies manager" /></SelectTrigger>
                <SelectContent>{members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {form.paid_by_type === "player" && (
              <Select value={form.paid_by_player_id} onValueChange={(v) => setForm({ ...form, paid_by_player_id: v })}>
                <SelectTrigger className="mt-2" data-testid="player-select"><SelectValue placeholder="Kies speler" /></SelectTrigger>
                <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {form.paid_by_type === "other" && (
              <Input className="mt-2" placeholder="bv. Marieke (moeder van Tim)" value={form.paid_by_name} onChange={(e) => setForm({ ...form, paid_by_name: e.target.value })} data-testid="other-name-input" />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.is_deposit} onCheckedChange={(c) => setForm({ ...form, is_deposit: !!c })} data-testid="is-deposit-checkbox" />
            <span>Dit is een <strong>borg</strong> (krijgen we terug — telt niet mee voor de spelers)</span>
          </label>
          <div>
            <Label>Notitie (optioneel)</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="expense-note" />
          </div>
          <div>
            <Label>Bon (optioneel, max 500 KB)</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} className="text-xs" data-testid="expense-receipt-input" />
              {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              {form.receipt_data_url && !uploading && (
                <a href={form.receipt_data_url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 hover:underline">Bekijken</a>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} data-testid="save-expense-btn">{saving ? "Opslaan..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Payment dialog ----------------
const PaymentDialog = ({ open, onClose, onSaved, seasonId, players, editing }) => {
  const [form, setForm] = useState({ date: today(), player_id: "", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { date: editing.date || today(), player_id: editing.player_id || "", amount: String(editing.amount ?? ""), note: editing.note || "" }
        : { date: today(), player_id: "", amount: "", note: "" });
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.player_id || !form.amount) { toast.error("Kies speler en bedrag"); return; }
    setSaving(true);
    try {
      const payload = {
        season_id: seasonId,
        player_id: form.player_id,
        amount: parseFloat(String(form.amount).replace(",", ".")),
        date: form.date,
        note: form.note?.trim() || null,
      };
      if (editing) {
        await axios.put(`${API}/payments/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/payments`, payload);
      }
      toast.success("Betaling opgeslagen");
      onClose();
      await onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Opslaan mislukt");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="payment-dialog">
        <DialogHeader><DialogTitle>{editing ? "Betaling bewerken" : "Nieuwe betaling"}</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Datum</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" />
            </div>
            <div>
              <Label>Bedrag (€)</Label>
              <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" />
            </div>
          </div>
          <div>
            <Label>Speler</Label>
            <Select value={form.player_id} onValueChange={(v) => setForm({ ...form, player_id: v })}>
              <SelectTrigger data-testid="payment-player-select"><SelectValue placeholder="Kies speler" /></SelectTrigger>
              <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notitie (optioneel)</Label>
            <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="bv. Tikkie 12 sep" data-testid="payment-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} data-testid="save-payment-btn">{saving ? "Opslaan..." : "Opslaan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Expenses;
